import React, { useMemo } from 'react';
import { useColorModeValue, useToken } from '@chakra-ui/react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  ComposedChart,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { DataBox } from 'src/components/molecules/DataBox';
import { KeyMetricsDateFormatter } from 'src/utils/TickFormatters';
import { useChartEffectTickFormatter } from 'src/hooks/useEffectFormatter';
import { ApiForecast } from 'src/api/queries';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';

type ForecastChartProps = {
  forecast: ApiForecast;
};

// Chakra UI default color scale values (blue.500, red.500, green.500, yellow.500, purple.500, orange.500)
const SCENARIO_COLORS = [
  '#3182CE',
  '#E53E3E',
  '#38A169',
  '#D69E2E',
  '#805AD5',
  '#DD6B20',
];

export const ForecastChart: React.FC<ForecastChartProps> = ({ forecast }) => {
  // The lower confidence-band Area is an opaque mask that erases the upper
  // band's fill below the lower bound — it must match the panel background
  const [panelDark, borderDark] = useToken('colors', [
    'figmaDark.panel',
    'figmaDark.borderSubtle',
  ]);
  const bandMaskFill = useColorModeValue('white', panelDark);
  const tooltipBg = useColorModeValue('white', panelDark);
  const tooltipBorder = useColorModeValue('#ccc', borderDark);
  const tooltipText = useColorModeValue('#14171F', '#ECEDEE');
  const actualLineStroke = useColorModeValue('#1A202C', '#ECEDEE');
  const { metricLabel } = useKpiMetricLabels();
  // Currency-aware for monetary KPIs (2.60K zł), plain count otherwise —
  // same formatter the dashboards use for effect axes
  const effectTickFormatter = useChartEffectTickFormatter();
  const { chartData, forecastStartDate } = useMemo(() => {
    if (forecast.scenarios.length === 0)
      return { chartData: [], forecastStartDate: '' };

    // Build historical data points
    const historicalRows = (forecast.historicalSales ?? []).map((h) => {
      const row: Record<string, number | string> = {
        date: h.date,
        actual: h.actual,
        predicted: h.predicted,
      };
      return row;
    });

    // Build forecast data points
    const dateSet = new Set<string>();
    forecast.scenarios.forEach((s) =>
      s.results.forEach((r) => dateSet.add(r.periodDate)),
    );
    const forecastDates = Array.from(dateSet).sort();
    const startDate = forecastDates[0] ?? '';

    const forecastRows = forecastDates.map((date) => {
      const row: Record<string, number | string> = { date };
      forecast.scenarios.forEach((scenario, idx) => {
        const result = scenario.results.find((r) => r.periodDate === date);
        if (result) {
          row[`sales_${idx}`] = result.predictedSales;
          row[`lower_${idx}`] = result.lowerBound;
          row[`upper_${idx}`] = result.upperBound;
          row[`baseline_${idx}`] = result.baseline;
        }
      });
      return row;
    });

    // Add a connector line between last historical point and first forecast point.
    // Connects last actual to the average of all scenarios' first predicted values.
    const allRows = [...historicalRows, ...forecastRows];
    if (historicalRows.length > 0 && forecastRows.length > 0) {
      const lastHist = historicalRows[historicalRows.length - 1];
      const firstFc = forecastRows[0];
      lastHist.connector = Number(lastHist.actual);
      // Average of all scenarios' first period predicted sales
      const scenarioKeys = Object.keys(firstFc).filter((k) =>
        k.startsWith('sales_'),
      );
      const avg =
        scenarioKeys.length > 0
          ? scenarioKeys.reduce((sum, k) => sum + Number(firstFc[k]), 0) /
            scenarioKeys.length
          : 0;
      firstFc.connector = avg;
    }

    return {
      chartData: allRows,
      forecastStartDate: startDate,
    };
  }, [forecast]);

  if (chartData.length === 0) return null;

  return (
    <DataBox
      title={`${metricLabel} Forecast`}
      colSpan={12}
      minH="400px"
      isError={false}
      isLoading={false}
      hasData={chartData.length > 0}>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis
            dataKey="date"
            tickFormatter={KeyMetricsDateFormatter}
            fontSize={12}
          />
          <YAxis tickFormatter={effectTickFormatter} fontSize={12} />
          <Tooltip
            content={({ label, payload }) => {
              if (!payload || payload.length === 0) return null;
              const visibleItems = payload.filter((p) => {
                const key = p.dataKey?.toString() ?? '';
                return (
                  key === 'actual' ||
                  key === 'predicted' ||
                  key.startsWith('sales_')
                );
              });
              if (visibleItems.length === 0) return null;
              return (
                <div
                  style={{
                    background: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    padding: '8px 12px',
                    borderRadius: 4,
                  }}>
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 4,
                      color: tooltipText,
                    }}>
                    {KeyMetricsDateFormatter(label)}
                  </div>
                  {visibleItems.map((item) => (
                    <div key={item.dataKey} style={{ color: item.color }}>
                      {item.name}:{' '}
                      {Number(item.value).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  ))}
                </div>
              );
            }}
          />
          <Legend />

          {/* Vertical line separating history from forecast */}
          {forecastStartDate && (
            <ReferenceLine
              x={forecastStartDate}
              stroke="#A0AEC0"
              strokeDasharray="4 4"
              label=""
            />
          )}

          {/* Historical: actual sales (solid black) */}
          <Line
            type="monotone"
            dataKey="actual"
            stroke={actualLineStroke}
            strokeWidth={2}
            dot={false}
            name="Actual"
            connectNulls={false}
          />

          {/* Connector: dotted line bridging history to forecast */}
          <Line
            type="monotone"
            dataKey="connector"
            stroke="#A0AEC0"
            strokeWidth={1}
            strokeDasharray="6 4"
            dot={false}
            connectNulls
            legendType="none"
          />

          {/* Historical: model predicted sales (dashed gray) */}
          <Line
            type="monotone"
            dataKey="predicted"
            stroke="#A0AEC0"
            strokeWidth={1}
            strokeDasharray="4 4"
            dot={false}
            name="Model fit"
            connectNulls={false}
          />

          {/* Forecast scenarios — confidence bands only for ≤2 scenarios */}
          {forecast.scenarios.map((scenario, idx) => (
            <React.Fragment key={scenario.scenarioId}>
              {forecast.scenarios.length <= 2 && (
                <>
                  <Area
                    type="monotone"
                    dataKey={`upper_${idx}`}
                    stroke="none"
                    fill={SCENARIO_COLORS[idx % SCENARIO_COLORS.length]}
                    fillOpacity={0.1}
                    name={`${scenario.scenarioName} (upper)`}
                    legendType="none"
                  />
                  <Area
                    type="monotone"
                    dataKey={`lower_${idx}`}
                    stroke="none"
                    fill={bandMaskFill}
                    fillOpacity={1}
                    name={`${scenario.scenarioName} (lower)`}
                    legendType="none"
                  />
                </>
              )}
              <Line
                type="monotone"
                dataKey={`sales_${idx}`}
                stroke={SCENARIO_COLORS[idx % SCENARIO_COLORS.length]}
                strokeWidth={2}
                dot={false}
                name={scenario.scenarioName}
              />
              {forecast.scenarios.length <= 2 && (
                <Line
                  type="monotone"
                  dataKey={`baseline_${idx}`}
                  stroke={SCENARIO_COLORS[idx % SCENARIO_COLORS.length]}
                  strokeWidth={1}
                  strokeDasharray="5 5"
                  dot={false}
                  name={`${scenario.scenarioName} (baseline)`}
                  legendType="none"
                />
              )}
            </React.Fragment>
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </DataBox>
  );
};
